import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  Filters,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import { DataService } from 'src/app/components/totali-cruscotto/data-service.interface';
import { Ambito, AmbitoDoppiaSurvey } from '../ambiti/ambiti.service';
import { ESRS } from '../ESRS/esrs.service';
import { Impatto, ScritturaImpatto } from '../impatto/impatto.service';
import { Domande, Opzioni, Sezioni } from '../informativa-aziendale/informativa-aziendale.service';
import { Settore, SottoSettore } from '../settori/settori.service';
import { CategoriaStakeholders, Stakeholder } from '../stakeholder/stakeholder.service';
import { DoppiaMaterialitaWebService } from './doppia-materialita-web.service';

export interface MailRiepilogoDTO {
  id: string;
  nomeSh: string;
  cognomeSh: string;
  ragSocSh: string;
  emailSh: string;
  dataInvio: string;
  esitoInvio: boolean;
}

export interface DoppiaMaterialita {
  id: string;
  titolo: string;
  idQuestionario: string;
  idAzienda: string;
  fatturato: number;
  anno: string;
  nrDipendenti: number;
  mailInvio: string;
  mailNotifica: string;
  landingPage: string;
  lastStep: number;
  tipoSettore: 'SPECIFICO' | 'GENERALE';
  settori: Settore[];
  sottoSettori: SottoSettore[];
  stato: 'BOZZA' | 'PUBBLICATO' | 'ARCHIVIATO';
  dataUltimaModifica: string;

  sogliaInterni: number;
  sogliaEsterni: number;
  shInterni: CategoriaStakeholders[];
  shEsterni: CategoriaStakeholders[];
  validatoriIro: CategoriaStakeholders[];
  impattiIRO: ImpattoIro[];
  rischi: Rischio[];
  opportunita: Opportunita[];
  statoDm: 'NON_INVIATO' | 'IN_VALIDAZIONE' | 'APPROVATO' | 'NON_APPROVATO';

  pesoShInterni?: number;
  pesoShEsterni?: number;
}

export interface QuestionarioValidazioneIro {
  id?: string;
  tipo: string;
  titolo: string;
  chiave: string;
  codiceLingua: string;
  descrizione: string;
  idInformativa: string;
  sezioni: SezioniValidazioneIro[];
  stato: string;
  versione: number;
}

export interface VersioneValidazione {
  chiave: string;
  dataPubblicazione: string;
  descrizione: string;
  id: string;
  titolo: string;
  versione: number;
}

export interface ImpattoIro extends Impatto {
  idAmbito: string;
  idTematica: string;
  idTema?: string;
  orizzontiTemporali: string[];
  impattoDirettoIndiretto?: string[];
  selected: boolean;
  validato: boolean;
  compilazioni?: InfoCompilazione[]; // Solo nella get get-nested-iro
  entita?: number;
  portata?: number;
  irrimediabilita?: number;
  possibilitaAccadimento?: number;
}

export interface Rischio {
  id: string;
  idAmbito: string;
  idTematica: string;
  idTema?: string;
  descrizione: string;
  fonte?: string;
  orizzontiTemporali?: string[];
  catene: string[];
  selected: boolean;
  validato: boolean;
  compilazioni?: InfoCompilazione[]; // Solo nella get get-nested-iro
  severita?: number;
  probabilita?: number;
}

export interface Opportunita {
  id: string;
  idAmbito: string;
  idTematica: string;
  idTema?: string;
  descrizione: string;
  fonte?: string;
  orizzontiTemporali?: string[];
  catene: string[];
  selected: boolean;
  validato: boolean;
  compilazioni?: InfoCompilazione[]; // Solo nella get get-nested-iro
  severita?: number;
  probabilita?: number;
}

export interface ElementiCheckati {
  idAmbito: string;
  idTematica: string;
  idTema: string | undefined;
  idElementi: string[];
}

export interface InfoCompilazione {
  idValidatore: string;
  nomeValidatore: string;
  rispostaData: string;
  validato: boolean;
}

export interface SezioniValidazioneIro extends Sezioni {
  domande: DomandeValidazioneIro[];
}

/**
 * oggetto domanda con index originale che aveva nell'array prima dell'annidamento
 */
export interface DomandeValidazioneIro extends Domande {
  idxOriginale: number;
  idTematica?: string;
  infoTematica?: string;
  valid?: boolean;
  opzioni: OpzioniValidazioneIro[];
}

export interface OpzioniValidazioneIro extends Opzioni {
  valid?: boolean;
  /**
   * Oggetti delle sottodomande - calcolato manualmente solo in determinati contesti
   */
  objSottodomande?: DomandeValidazioneIro[];
}
export interface DoppiaSurvey {
  id: string;
  dataCompilazione?: string;
  materialita: string;
  titolo: string;
  ambiti: AmbitoDoppiaSurvey[];
  sottoSettori: SottoSettore[];
  settori: Settore[];
  tipo: string;
  aziende: string[];
  stato: string;
  chiave: string;
  codiceLingua: string;
  versione: number;
}

export interface CategoriaStakeholderIRO {
  id: string;
  idAzienda: string;
  impattiAssegnati: ImpattoIro[];
  opportunitaAssegnate: Opportunita[];
  peso: number;
  rischiAssegnati: Rischio[];
  sceltaCampione: string;
  selected: boolean;
  stakeholders: Stakeholder[];
  titolo: string;
}

export interface DatiConsolidati {
  sogliaImpatti: number;
  sogliaRischi: number;
  sogliaOpportunita: number;
  soloIRO: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DoppiaMaterialitaService implements DataService {
  getTotaliCruscottoDS(idModulo: string, isIro?: boolean): Observable<any> {
    if (isIro) {
      return this.getTotaliValidazioni(idModulo);
    }

    return this.getTotaliCruscottoSurvey(idModulo);
  }

  constructor(private readonly doppiaMaterialitaWebService: DoppiaMaterialitaWebService) {}

  public getCatena(): Observable<ScritturaImpatto[]> {
    return of([
      {
        codice: 'AZIENDA',
        descrizione: 'In azienda',
      },
      {
        codice: 'MONTE',
        descrizione: 'A monte',
      },
      {
        codice: 'VALLE',
        descrizione: 'A valle',
      },
    ]);
  }

  public getImpattoDirIndir(): Observable<ScritturaImpatto[]> {
    return of([
      {
        codice: 'Diretto',
        descrizione: 'Impatto Diretto',
      },
      {
        codice: 'Indiretto',
        descrizione: 'Impatto Indiretto',
      },
    ]);
  }

  public getImpattoOrizTemp(): Observable<ScritturaImpatto[]> {
    return of([
      {
        codice: 'BREVE',
        descrizione: 'Breve termine',
      },
      {
        codice: 'MEDIO',
        descrizione: 'Medio termine',
      },
      {
        codice: 'LUNGO',
        descrizione: 'Lungo termine',
      },
    ]);
  }

  public calcolaStringaInfoImpattoIro(impatto: ImpattoIro): string {
    return [
      (impatto.impattoPotenzialeEffettivo || []).join('/'),
      (impatto.impattoPositivoNegativo || []).join('/'),
      (impatto.orizzontiTemporali || []).join('/'),
      (impatto.impattoDirettoIndiretto || []).join('/'),
    ]
      .filter((stringhe) => !!stringhe)
      .join(' - ');
  }

  public calcolaStringaInfoRischio(rischio: Rischio): string {
    return [
      rischio.fonte || '',
      (rischio.orizzontiTemporali || []).join('/'),
      (rischio.catene || []).join('/'),
    ]
      .filter((stringhe) => !!stringhe)
      .join(' - ');
  }

  public calcolaStringaInfoOpportunita(opportunita: Opportunita): string {
    return [
      opportunita.fonte || '',
      (opportunita.orizzontiTemporali || []).join('/'),
      (opportunita.catene || []).join('/'),
    ]
      .filter((stringhe) => !!stringhe)
      .join(' - ');
  }

  /**
   * Estrae la lista paginata delle doppie materialità secondo i filtri spefcificati.
   *
   * @param page pagina
   * @param pageSize record per pagina
   * @param ricerca risultato per ricerca
   * @param filters valori da filtrare
   * @param sortBy sort asc desc
   * @returns lista di righe
   */
  public getDoppiaMaterialita(
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.doppiaMaterialitaWebService.get(page, pageSize, ricerca, filters, sortBy);
  }

  /**
   * Crea la doppia materialità
   * @param data dati da inserire nel MongoDB
   * @returns chiama la funzione post Materialita per la creazione Materialità
   */
  public postDoppiaMaterialita(data: any) {
    return this.doppiaMaterialitaWebService.post(data);
  }

  /**
   * Elimina la doppia materialità specificata
   * @param id id della doppia materialità da eliminare
   * @returns
   */

  public delete(idModulo: any): Observable<any> {
    return this.doppiaMaterialitaWebService.delete(idModulo);
  }

  public archivia(idModulo: any) {
    return this.doppiaMaterialitaWebService.archivia(idModulo);
  }

  public disarchivia(idModulo: any, idSettore: string) {
    return this.doppiaMaterialitaWebService.disarchivia(idModulo, idSettore);
  }

  /**
   * Materialita
   * @param id
   * @param materialita
   * @returns
   */
  public putDoppiaMaterialita(id: string, materialita: any) {
    return this.doppiaMaterialitaWebService.put(id, materialita);
  }

  /**
   * Ritorna materialità corrispondete all'id
   * @param idDoppiaMaterialita
   * @returns
   */
  public getDoppiaMaterialitaById(id: string) {
    return this.doppiaMaterialitaWebService.getDoppiaMaterialitaById(id);
  }

  /* ------------------------- T E M A T I C H E  -------------------------------- */

  public getQuestionarioDoppiaMaterialita(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getQuestionarioDoppiaMaterialita(idDoppiaMaterialita);
  }

  public putQuestionarioDoppiaMaterialita(
    idDoppiaMaterialita: string,
    idSurvey: string,
    ambiti: Ambito[],
    settori: Settore[],
    titolo: string,
    stato: string,
    tipo: string,
  ) {
    return this.doppiaMaterialitaWebService.putQuestionarioDoppiaMaterialita(
      idDoppiaMaterialita,
      idSurvey,
      ambiti,
      settori,
      titolo,
      stato,
      tipo,
    );
  }

  public getEsrsByIdDP(idDoppiaMaterialita: string, idAmbito: string, idTematica: string) {
    return this.doppiaMaterialitaWebService.getEsrsByIdDP(
      idDoppiaMaterialita,
      idAmbito,
      idTematica,
    );
  }

  public putEsrsTematicaDP(
    idDoppiaMaterialita: string,
    idAmbito: string,
    idTematica: string,
    esrs: ESRS[],
  ) {
    return this.doppiaMaterialitaWebService.putEsrsTematicaDP(
      idDoppiaMaterialita,
      idAmbito,
      idTematica,
      esrs,
    );
  }

  /* ------------------------- I R O -------------------------------- */

  public getImpatti(
    idDoppiaMaterialita: string,
    idAmbito: string,
    idTematica: string,
    idTema?: string,
  ) {
    return this.doppiaMaterialitaWebService.getImpatti(
      idDoppiaMaterialita,
      idAmbito,
      idTematica,
      idTema,
    );
  }

  public getRischi(
    idDoppiaMaterialita: string,
    idAmbito: string,
    idTematica: string,
    idTema?: string,
  ) {
    return this.doppiaMaterialitaWebService.getRischi(
      idDoppiaMaterialita,
      idAmbito,
      idTematica,
      idTema,
    );
  }

  public getOpportunita(
    idDoppiaMaterialita: string,
    idAmbito: string,
    idTematica: string,
    idTema?: string,
  ) {
    return this.doppiaMaterialitaWebService.getOpportunita(
      idDoppiaMaterialita,
      idAmbito,
      idTematica,
      idTema,
    );
  }

  public putImpattiEffimeri(idDoppiaMaterialita: string, impatto: ImpattoIro) {
    return this.doppiaMaterialitaWebService.putImpattiEffimeri(idDoppiaMaterialita, impatto);
  }

  public putRischiEffimeri(idDoppiaMaterialita: string, rischio: Rischio) {
    return this.doppiaMaterialitaWebService.putRischiEffimeri(idDoppiaMaterialita, rischio);
  }

  public putOpportunitaEffimeri(idDoppiaMaterialita: string, opportunita: Opportunita) {
    return this.doppiaMaterialitaWebService.putOpportunitaEffimeri(
      idDoppiaMaterialita,
      opportunita,
    );
  }

  public putCheckImpattiEffimeri(idDoppiaMaterialita: string, impattiCheckati: ElementiCheckati[]) {
    return this.doppiaMaterialitaWebService.putCheckImpattiEffimeri(
      idDoppiaMaterialita,
      impattiCheckati,
    );
  }

  public putCheckRischiEffimeri(idDoppiaMaterialita: string, rischiCheckati: ElementiCheckati[]) {
    return this.doppiaMaterialitaWebService.putCheckRischiEffimeri(
      idDoppiaMaterialita,
      rischiCheckati,
    );
  }

  public putCheckOpportunitaEffimeri(
    idDoppiaMaterialita: string,
    opportunitaCheckati: ElementiCheckati[],
  ) {
    return this.doppiaMaterialitaWebService.putCheckOpportunitaEffimeri(
      idDoppiaMaterialita,
      opportunitaCheckati,
    );
  }

  public getValidatoriIro(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getValidatoriIro(idDoppiaMaterialita);
  }

  public checkValidatoriIro(
    idDoppiaMaterialita: string,
    categorieStakeholders: CategoriaStakeholders[],
  ) {
    return this.doppiaMaterialitaWebService.checkValidatoriIro(
      idDoppiaMaterialita,
      categorieStakeholders,
    );
  }

  public validaIroSelezionati(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.validaIroSelezionati(idDoppiaMaterialita);
  }

  public listCruscottoValidatori(
    idDoppiaMaterialita: string,
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.doppiaMaterialitaWebService.listCruscottoValidatori(
      idDoppiaMaterialita,
      page,
      pageSize,
      ricerca,
      filters,
      sortBy,
    );
  }

  public getTemplateMailValidazioni(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getTemplateMailValidazioni(idDoppiaMaterialita);
  }

  public updateTemplateMailValidazioni(idDoppiaMaterialita: string, templateMail: any) {
    return this.doppiaMaterialitaWebService.updateTemplateMailValidazioni(
      idDoppiaMaterialita,
      templateMail,
    );
  }

  public testInvioMailValidazioni(messaggio: string, email: string, idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.testInvioMailValidazioni(
      messaggio,
      email,
      idDoppiaMaterialita,
    );
  }

  public getTemplateMail(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getTemplateMail(idDoppiaMaterialita);
  }

  public updateTemplateMail(idDoppiaMaterialita: string, templateMail: any) {
    return this.doppiaMaterialitaWebService.updateTemplateMail(idDoppiaMaterialita, templateMail);
  }

  public testInvioMail(messaggio: string, email: string, idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.testInvioMail(messaggio, email, idDoppiaMaterialita);
  }

  public getTotaliValidazioni(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getTotaliValidazioni(idDoppiaMaterialita);
  }

  public getVersioniQuestionarioValidazione(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getVersioniQuestionarioValidazione(idDoppiaMaterialita);
  }

  public inviaMailValidatori(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.inviaMailValidatori(idDoppiaMaterialita);
  }

  public inviaMailValidatoreSingolo(idDoppiaMaterialita: string, idValidatore: string) {
    return this.doppiaMaterialitaWebService.inviaMailValidatoreSingolo(
      idDoppiaMaterialita,
      idValidatore,
    );
  }

  public inviaNotificheValidatori(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.inviaNotificheValidatori(idDoppiaMaterialita);
  }

  public inviaNotificaValidatoreSingolo(idDoppiaMaterialita: string, idValidatore: string) {
    return this.doppiaMaterialitaWebService.inviaNotificaValidatoreSingolo(
      idDoppiaMaterialita,
      idValidatore,
    );
  }

  public pubblicaQuestionarioValidazione(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.pubblicaQuestionarioValidazione(idDoppiaMaterialita);
  }

  public getQuestionarioValidazioneDaCompilare(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getQuestionarioValidazioneDaCompilare(
      idDoppiaMaterialita,
    );
  }

  public getAnteprimaQuestionarioValidazioneDaCompilare(
    idAzienda: string,
    idDoppiaMaterialita: string,
  ) {
    return this.doppiaMaterialitaWebService.getAnteprimaQuestionarioValidazioneDaCompilare(
      idAzienda,
      idDoppiaMaterialita,
    );
  }

  public salvaQuestionarioValidazione(
    idDoppiaMaterialita: string,
    idValidatore: string,
    questionario: any,
  ) {
    return this.doppiaMaterialitaWebService.salvaQuestionarioValidazione(
      idDoppiaMaterialita,
      idValidatore,
      questionario,
    );
  }

  public getQuestionarioValidazioneCompilato(idDoppiaMaterialita: string, idValidatore: string) {
    return this.doppiaMaterialitaWebService.getQuestionarioValidazioneCompilato(
      idDoppiaMaterialita,
      idValidatore,
    );
  }

  public exportVersioneValidazione(idDoppiaMaterialita: string, versione: number) {
    return this.doppiaMaterialitaWebService.exportVersioneValidazione(
      idDoppiaMaterialita,
      versione,
    );
  }

  public getQuestionarioValidazioneDaEsterno(temporaryToken: string) {
    return this.doppiaMaterialitaWebService.getQuestionarioValidazioneDaEsterno(temporaryToken);
  }

  public salvaQuestionarioValidazioneDaEsterno(temporaryToken: string, questionario: any) {
    return this.doppiaMaterialitaWebService.salvaQuestionarioValidazioneDaEsterno(
      temporaryToken,
      questionario,
    );
  }

  public getStakeholders(idDoppiaMaterialita: string, tipo: 'INTERNI' | 'ESTERNI') {
    return this.doppiaMaterialitaWebService.getStakeholders(idDoppiaMaterialita, tipo);
  }

  public putStakeholders(
    idDoppiaMaterialita: string,
    tipo: 'INTERNI' | 'ESTERNI',
    stakeholders: any,
  ) {
    return this.doppiaMaterialitaWebService.putStakeholders(
      idDoppiaMaterialita,
      tipo,
      stakeholders,
    );
  }

  public salvaPesiStakeholders(
    idDoppiaMaterialita: string,
    pesiStakeholders: { pesoShInterni: number; pesoShEsterni: number },
  ) {
    return this.doppiaMaterialitaWebService.salvaPesiStakeholders(
      idDoppiaMaterialita,
      pesiStakeholders,
    );
  }

  public salvaSceltaCampione(
    idDoppiaMaterialita: string,
    tipologia: string,
    idCategoria: string,
    sceltaCampione: string,
  ) {
    return this.doppiaMaterialitaWebService.salvaSceltaCampione(
      idDoppiaMaterialita,
      tipologia,
      idCategoria,
      sceltaCampione,
    );
  }

  public getEsitiCompilazioniUltimaVersione(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getEsitiCompilazioniUltimaVersione(idDoppiaMaterialita);
  }

  public getEsitiCompilazioniPerVersione(idDoppiaMaterialita: string, versione: number) {
    return this.doppiaMaterialitaWebService.getEsitiCompilazioniPerVersione(
      idDoppiaMaterialita,
      versione,
    );
  }

  /**
   * Ritorna materialità corrispondete all'id
   * @param idDoppiaMaterialita
   * @returns
   */
  public getSurveyDP(id: string) {
    return this.doppiaMaterialitaWebService.getSurveyDP(id);
  }
  public getCategorieSHperIRO(idDoppiaMaterialita: string, idIro: string) {
    return this.doppiaMaterialitaWebService.getCategorieSHperIRO(idDoppiaMaterialita, idIro);
  }

  public putCategorieSHperIRO(objSalvataggio: any) {
    return this.doppiaMaterialitaWebService.putCategorieSHperIRO(objSalvataggio);
  }
  public validaCategorieSHperIRO(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.validaCategorieSHperIRO(idDoppiaMaterialita);
  }

  public getCriteriPerAssegnazione(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getCriteriPerAssegnazione(idDoppiaMaterialita);
  }

  public putCriteriAssegnazioniCategoria(
    idDoppiaMaterialita: string,
    idCategoria: string,
    tipologia: string,
    testoCriterio: string,
  ) {
    return this.doppiaMaterialitaWebService.putCriteriAssegnazioniCategoria(
      idDoppiaMaterialita,
      idCategoria,
      tipologia,
      testoCriterio,
    );
  }

  public getCruscottoSurvey(
    idDoppiaMaterialita: string,
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.doppiaMaterialitaWebService.getCruscottoSurvey(
      idDoppiaMaterialita,
      page,
      pageSize,
      ricerca,
      filters,
      sortBy,
    );
  }

  public getRiepilogoInvioMail(
    idDoppiaMaterialita: string,
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.doppiaMaterialitaWebService.getRiepilogoInvioMail(
      idDoppiaMaterialita,
      page,
      pageSize,
      ricerca,
      filters,
      sortBy,
    );
  }

  public postInvioMailSingoloSurvey(idDoppiaMaterialita: string, id: string) {
    return this.doppiaMaterialitaWebService.postInvioMailSingoloSurvey(idDoppiaMaterialita, id);
  }

  public salvaScadenzaSurvey(idDoppiaMaterialita: string, scadenzaSurvey: Date) {
    return this.doppiaMaterialitaWebService.putScadenzaSurvey(idDoppiaMaterialita, scadenzaSurvey);
  }

  public getTotaliCruscottoSurvey(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getTotaliCruscottoSurvey(idDoppiaMaterialita);
  }

  public putTemplateMailSurvey(idDoppiaMaterialita: string, templateMail: any) {
    return this.doppiaMaterialitaWebService.putTemplateMailSurvey(
      idDoppiaMaterialita,
      templateMail,
    );
  }

  public getTemplateMailSurvey(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getTemplateMailSurvey(idDoppiaMaterialita);
  }

  public getAnteprimaMaterialitaCompilataSurvey(
    idDoppiaMaterialita: string,
    idAzienda: string,
    idSurvey: string,
  ) {
    return this.doppiaMaterialitaWebService.getAnteprimaMaterialitaCompilataSurvey(
      idDoppiaMaterialita,
      idAzienda,
      idSurvey,
    );
  }

  /**
   * Metodo che invia mail a tutti gli stakeholder che la devono ricevere (pre prendere il token e fare la compilazione esterna della survey)
   * @param idDoppiaMaterialita
   */
  public putInviaNotificaMultiploSurvey(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.putInviaNotificaMultiploSurvey(idDoppiaMaterialita);
  }

  /**
   * Metodo che invia mail a tutti gli stakeholder che la devono ricevere (pre prendere il token e fare la compilazione esterna della survey)
   * @param idDoppiaMaterialita
   */
  public postInvioMailMultiploSurvey(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.postInvioMailMultiploSurvey(idDoppiaMaterialita);
  }

  /* ---------- DOPPIA SURVEY -------------- */

  public getDatiDoppiaSurvey(idDoppiaMaterialita: string, idCategoriaStakeholder: string) {
    return this.doppiaMaterialitaWebService.getDatiSurvey(
      idDoppiaMaterialita,
      idCategoriaStakeholder,
    );
  }

  public getDoppiaSurveyCompilata(idDoppiaMaterialita: string, idSurveyCompilato: string) {
    return this.doppiaMaterialitaWebService.getDoppiaSurveyCompilata(
      idDoppiaMaterialita,
      idSurveyCompilato,
    );
  }

  public getRisultati(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getRisultati(idDoppiaMaterialita);
  }

  public consolidaRisultati(idDoppiaMaterialita: string, datiConsolidati: DatiConsolidati) {
    return this.doppiaMaterialitaWebService.consolidaRisultati(
      idDoppiaMaterialita,
      datiConsolidati,
    );
  }

  public postDatiDoppiaSurvey(
    idDoppiaMaterialita: string,
    idStakeholder: string,
    idSurvey: string,
    surveyCompilata: DoppiaSurvey,
  ) {
    return this.doppiaMaterialitaWebService.postDatiDoppiaSurvey(
      idDoppiaMaterialita,
      idStakeholder,
      idSurvey,
      surveyCompilata,
    );
  }

  /**
   * Ritorna la Survey della Materialità associata per la quale è stata richiesta la compilazione
   * @param token informazioni sono reperite all'interno del temporary token ( dura 24h )
   * @returns
   */
  public getDoppiaSurveyEsterna(token: string) {
    return this.doppiaMaterialitaWebService.getDoppiaSurveyEsterna(token);
  }

  /**
   * Metodo che mi controlla se tutte le risposte sono state compilate per la doppia survey
   * @param survey
   * @returns
   */
  isElementoNonCompilato(survey: DoppiaSurvey): boolean {
    return survey.ambiti.some((ambito) =>
      ambito.tematiche.some((tematica) => {
        const livelloIRO = tematica?.temiIro?.length ? tematica?.temiIro : [tematica];

        return livelloIRO.some((temaOrTematica) => {
          const impattoNonCompilato = temaOrTematica.impattiIro?.some(
            (imp) =>
              !imp.entita ||
              !imp.portata ||
              (imp.impattoPositivoNegativo?.includes('Negativo') && !imp.irrimediabilita) ||
              (imp.impattoPotenzialeEffettivo?.includes('Potenziale') &&
                !imp.possibilitaAccadimento),
          );
          const rischioNonCompilato = temaOrTematica.rischi?.some(
            (rischio) => !rischio.severita || !rischio.probabilita,
          );
          const opportNonCompilato = temaOrTematica.opportunita?.some(
            (opport) => !opport.severita || !opport.probabilita,
          );

          let el = document.getElementById(tematica.id);
          el?.scrollIntoView({ behavior: 'smooth' });

          return impattoNonCompilato || rischioNonCompilato || opportNonCompilato;
        });
      }),
    );
  }

  /**
   * Manda la Survey della Materialità compilata per la quale è stata richiesta la compilazione
   * @param token informazioni sono reperite all'interno del temporary token ( dura 24h )
   * @param survey la survey che è  stata compilata
   * @returns
   */
  public postDoppiaSurveyEsterna(token: string, survey: any) {
    return this.doppiaMaterialitaWebService.postDoppiaSurveyEsterna(token, survey);
  }

  public pubblicaDoppiaSurvey(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.pubblicaDoppiaSurvey(idDoppiaMaterialita);
  }

  public getValidazioneStepCruscottoSurvey(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.getValidazioneStepCruscottoSurvey(idDoppiaMaterialita);
  }

  public stampa(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.stampa(idDoppiaMaterialita);
  }

  /**
   * Metodo che mi crea le etichette dei temi / sottotemi / sottosottotemi
   */
  creaEtichettaESRS(doppiaSurvey: DoppiaSurvey | undefined) {
    if (!doppiaSurvey) {
      return;
    }

    let etichetteESRS: { [idTema: string]: string } = {};

    let idTemiSelezionati: { idAmbito: string; idTematica: string; idTema: string }[] = [];

    doppiaSurvey?.ambiti?.forEach((amb) =>
      amb.tematiche?.forEach((tem) =>
        tem.temiIro?.forEach((temaIRO) => {
          idTemiSelezionati.push({
            idAmbito: amb.id,
            idTematica: tem.id,
            idTema: temaIRO.id,
          });
        }),
      ),
    );

    idTemiSelezionati?.forEach((temaSelezionato) => {
      const ambito = doppiaSurvey?.ambiti.find((a) => a.id === temaSelezionato.idAmbito);
      if (!ambito) return;

      const tematica = ambito.tematiche.find((t) => t.id === temaSelezionato.idTematica);
      if (!tematica) return;

      tematica.esrs?.forEach((esr) => {
        if (esr.id === temaSelezionato.idTema) {
          etichetteESRS[temaSelezionato.idTema] = 'Tema';
          return;
        }

        esr.sottoTemi?.forEach((sotto) => {
          if (sotto.id === temaSelezionato.idTema) {
            etichetteESRS[temaSelezionato.idTema] = 'SottoTema';
            return;
          }

          sotto.sottoTemi?.forEach((sottoSotto) => {
            if (sottoSotto.id === temaSelezionato.idTema) {
              etichetteESRS[temaSelezionato.idTema] = 'Sotto-SottoTema';
            }
          });
        });
      });
    });

    return etichetteESRS;
  }

  /**
   *  ritorna materialità pubblicate
   * @returns
   */
  public getDoppiaMaterialitaPubblicate(idSettore: string, idSottoSettori: string[]) {
    return this.doppiaMaterialitaWebService.getDoppiaMaterialitaPubblicate(
      idSettore,
      idSottoSettori,
    );
  }

  public exportAnalisiRisultati(idDoppiaMaterialita: string) {
    return this.doppiaMaterialitaWebService.exportAnalisiRisultati(idDoppiaMaterialita);
  }
}
